<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>
  <p-confirmDialog />

  <header>
    <div class="headline row-start-center gap-[1rem]">
      <h1>{{ headerTitle() }}</h1>
      @if (!isNew()) {
        <p-tag [severity]="statusSeverity()" [value]="statusLabelText()" />
      }
    </div>
    <div class="buttons">
      <!-- #5223 — a draft has posted nothing, so it can be removed outright. Hidden until the record is
           saved (there is nothing to delete before that) and again once it leaves Draft. The API applies
           the same rule; this only keeps the button off screen when it would be refused. -->
      @if (isDraft() && currentId()) {
        <p-button [label]="'Delete' | translateWF: 'Delete'" icon="pi pi-trash" severity="danger" variant="outlined" (click)="confirmDelete()" />
      }

      <span class="separator"></span>
      @if (spawnedAdvanceId()) {
        <p-button [label]="'ApAdvanceRequest_ViewAdvance' | translateWF: 'View advance'" icon="pi pi-wallet" variant="outlined" (click)="openSpawnedAdvance()" />
      }
      @if (isEditable()) {
        <p-button [label]="'SaveDraft' | translateWF: 'Save draft'" icon="pi pi-save" variant="outlined" (click)="saveDraft()" />
      }
      @if (isDraft() && isPersisted()) {
        <p-button [label]="'ApAdvanceRequest_Submit' | translateWF: 'Submit'" icon="pi pi-send" (click)="submit()" />
      }
      @if (isPendingApproval() && isApprover) {
        <p-button [label]="'ApAdvanceRequest_Approve' | translateWF: 'Approve'" icon="pi pi-check" severity="success" (click)="approve()" />
        <p-button [label]="'ApAdvanceRequest_Reject' | translateWF: 'Reject'" icon="pi pi-times" severity="danger" variant="outlined" (click)="reject()" />
      }
      @if (isPersisted() && isEditable()) {
        <p-button [label]="'Cancel' | translateWF: 'Cancel'" icon="pi pi-ban" severity="secondary" variant="text" (click)="cancel()" />
      }
      <p-button [label]="'Back' | translateWF: 'Back'" variant="outlined" icon="pi pi-arrow-left" (click)="back()" />
    </div>
  </header>

  <div class="content">
    <form [formGroup]="form" class="grid-12 gap-[1.5rem]">
      <!-- LEFT COLUMN -->
      <div class="span-12-12-8 col">
        <!-- Request -->
        <p-panel [header]="'ApAdvanceRequest_Section' | translateWF: 'Advance Request'" [toggleable]="true">
          <p-card>
            <div class="grid-12">
              <div class="span-12-6">
                <div class="form-box">
                  <label for="provider">{{ 'ApPayment_Provider' | translateWF: 'Provider' }} <span class="text-red-500">*</span></label>
                  <!-- `class` sizes the host, `styleClass` the inner input — the typeahead needs BOTH
                     to fill its column, matching Payment Request details (#5227/#5228). -->
                  <p-autoComplete
                    id="provider"
                    formControlName="provider"
                    #providerSearch="cslServerSearch"
                    [cslServerSearch]="fetchProviders"
                    [suggestions]="providerSearch.results()"
                    (onSelect)="onProviderSelected($event.value)"
                    (onClear)="onProviderCleared()"
                    optionLabel="name"
                    [forceSelection]="true"
                    [dropdown]="true"
                    dropdownIcon="pi pi-search"
                    [minLength]="1"
                    appendTo="body"
                    class="w-full"
                    styleClass="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': form.controls.provider.invalid && form.controls.provider.touched }"
                  >
                    <ng-template let-business pTemplate="item">
                      <span
                        ><strong>{{ business.code }}</strong> — {{ business.name }}</span
                      >
                    </ng-template>
                  </p-autoComplete>
                  <small class="text-surface-500">{{ 'ApAdvanceRequest_Provider_Hint' | translateWF: 'The provider is locked once the request is created.' }}</small>
                </div>
              </div>
              <div class="span-12-6">
                <div class="form-box">
                  <label for="bank">{{ 'ApAdvanceRequest_DestinationBank' | translateWF: 'Destination bank' }}</label>
                  <p-select
                    id="bank"
                    formControlName="selectedProviderBankId"
                    [options]="bankOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [showClear]="true"
                    appendTo="body"
                    styleClass="w-full"
                    [placeholder]="'ApAdvanceRequest_SelectBank' | translateWF: 'Select a provider bank'"
                  />
                  <small class="text-surface-500">{{ 'ApAdvanceRequest_Bank_Hint' | translateWF: 'From the provider master; frozen as a snapshot on approval.' }}</small>
                  <!-- Said here because the destination freezes on approval and the request cannot be edited
                       afterwards: the advance would be the first place anyone found out. -->
                  @if (destinationWarning(); as warning) {
                    <p-message severity="warn" styleClass="w-full mt-2">{{ warning }}</p-message>
                  }
                </div>
              </div>
            </div>
          </p-card>
        </p-panel>

        <!-- Purchase orders -->
        <p-panel [toggleable]="true">
          <ng-template pTemplate="header">
            <div class="row-between-center w-full">
              <span class="font-semibold">{{ 'ApAdvanceRequest_Section_Pos' | translateWF: 'Purchase orders to advance' }} <span class="text-red-500">*</span></span>
              <span class="text-sm text-surface-500">{{ selectedCount() }} {{ 'ApAdvanceRequest_Selected' | translateWF: 'selected' }}</span>
            </div>
          </ng-template>

          <p-card data-variant="compact">
            <p-table [value]="lines.controls">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width: 3rem"></th>
                  <th>{{ 'ApAdvanceRequest_PurchaseOrder' | translateWF: 'Purchase order' }}</th>
                  <th class="text-right">{{ 'ApAdvanceRequest_Ordered' | translateWF: 'Ordered' }}</th>
                  <th class="text-right" style="width: 14rem">{{ 'ApAdvanceRequest_AmountToAdvance' | translateWF: 'Amount to advance' }}</th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-row>
                @let line = getRowGroup(row);
                <tr [formGroup]="line">
                  <td><p-checkbox formControlName="selected" [binary]="true" /></td>
                  <td>
                    <a class="text-primary cursor-pointer" (click)="openPurchaseOrder(line.controls.purchaseOrderReference.value)">
                      {{ line.controls.purchaseOrderNumber.value }} <i class="pi pi-external-link text-xs"></i>
                    </a>
                  </td>
                  <td class="text-right text-surface-500">{{ line.controls.ordered.value | number: '1.2-2' }} {{ currencyLabel() }}</td>
                  <td class="text-right">
                    <p-inputNumber formControlName="amount" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" [min]="0" styleClass="w-full" />
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="4">{{ 'ApAdvanceRequest_NoPos' | translateWF: 'Select a provider to load its purchase orders.' }}</td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        </p-panel>

        <!-- Notes -->
        <p-panel [header]="'ApPayment_Section_Notes' | translateWF: 'Notes'" [toggleable]="true" [collapsed]="true">
          <p-card>
            <div class="form-box">
              <textarea pTextarea formControlName="notes" rows="3" class="w-full" placeholder="Add a note…"></textarea>
            </div>
          </p-card>
        </p-panel>
      </div>

      <!-- RIGHT COLUMN -->
      <div class="span-12-12-4 col sticky-summary">
        <p-panel [header]="'ApAdvanceRequest_Summary' | translateWF: 'Request summary'">
          <p-card>
            <p class="text-sm text-surface-500 mb-3">
              {{ 'ApAdvanceRequest_Summary_Hint' | translateWF: 'On approval a draft advance is created for the executing department.' }}
            </p>
            <div class="ap-request-total">
              <span class="ap-request-total__label">{{ 'ApAdvanceRequest_Total' | translateWF: 'Total advance' }}</span>
              <span class="ap-request-total__value">{{ total() | number: '1.2-2' }} {{ currencyLabel() }}</span>
            </div>
          </p-card>
        </p-panel>
      </div>
    </form>
  </div>
</main>
